import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { MessageBox } from 'components/_new/MessageBox';
import { ErrorContainer } from 'components/_new/ErrorContainer/ErrorContainer';
import { Dialog, DialogBaseExtendProps } from 'components/_new/Dialog';
import { Loader } from 'components/_new/Loader';
import { KnowledgeBaseArticlePresentationResult } from 'types/KnowledgeBase';
import { useAuth } from 'contexts/authContext';
import { knowledgeBaseApi } from 'api/knowledgeBase';
import { ContentParserOptions } from 'utils/contentParser';
import { ArticlePresenterContent } from 'components/knowledgeBase/ArticlePresenterContent';
import { useScope } from 'contexts/scopeContext';

type ProgramArticlePresenterDialogContentProps = {
	articleQuery: UseQueryResult<KnowledgeBaseArticlePresentationResult>;
	notFoundAction: () => void;
	disableRating?: boolean;
	contentParserOptions?: ContentParserOptions;
};

const ProgramArticlePresenterDialogContent = ({
	articleQuery,
	disableRating = false,
	notFoundAction,
	contentParserOptions,
}: ProgramArticlePresenterDialogContentProps) => {
	const { t } = useTranslation();

	if (articleQuery.isError) {
		return (
			<ErrorContainer
				errorMessage={t('knowledgeBase.errorOccurred')}
				action={() => {
					articleQuery.refetch();
				}}
				error={articleQuery.error}
				meta={['presentationDialog']}
			/>
		);
	}

	if (articleQuery.isSuccess && articleQuery?.data) {
		if (!articleQuery.data.articleContent) {
			if (!articleQuery.data.alternates.length) {
				return (
					<ErrorContainer
						errorMessage={t('knowledgeBase.errorNotFound')}
						actionName={t('actions.close')}
						action={() => {
							notFoundAction();
						}}
					/>
				);
			}

			if (articleQuery.data.alternates.length > 0) {
				return <MessageBox message={t('knowledgeBase.articleIsNotAvailableInYourLanguage')} />;
			}
		}

		if (articleQuery.data.article) {
			return (
				<ArticlePresenterContent
					article={articleQuery.data.article}
					articleContent={articleQuery.data.articleContent}
					options={{ disableRating }}
					contentParserOptions={contentParserOptions}
				/>
			);
		}
	}

	return null;
};

type ProgramArticlePresenterDialogProps = DialogBaseExtendProps & {
	articleId: number | null;
	disableRating?: boolean;
	contentParserOptions?: ContentParserOptions;
};

export const ProgramArticlePresenterDialog = ({
	articleId,
	disableRating = false,
	contentParserOptions,
	...dialogProps
}: ProgramArticlePresenterDialogProps) => {
	const { currentUser } = useAuth();
	const { currentProgram: program } = useScope();

	const [currentArticleId, setCurrentArticleId] = useState(articleId);
	useEffect(() => {
		setCurrentArticleId(articleId);
		return () => {
			setCurrentArticleId(null);
		};
	}, [articleId]);

	const articleQuery = useQuery(
		[
			'KNOWLEDGE_BASE_ARTICLE',
			{
				userId: Number(currentUser?.id),
				articleId: currentArticleId,
				language: currentUser?.language,
				programId: Number(program?.id),
			},
		],
		() => knowledgeBaseApi.program.getProgramArticleByIdShow(Number(program?.id), Number(currentArticleId)),
		{
			enabled: Boolean(currentArticleId),
		}
	);

	return (
		<Dialog title={articleQuery.data?.articleContent.title || ''} size="lg" {...dialogProps}>
			{articleQuery.isLoading ? (
				<Loader />
			) : (
				<ProgramArticlePresenterDialogContent
					articleQuery={articleQuery}
					disableRating={disableRating}
					notFoundAction={() => {
						dialogProps?.onHide?.();
					}}
					contentParserOptions={{
						...contentParserOptions,
						handlers: {
							onArticleLinkClick: (newArticleId: number) => {
								setCurrentArticleId(newArticleId);
							},
						},
					}}
				/>
			)}
		</Dialog>
	);
};
